import React from 'react';
import { Users, Trophy, Save, RefreshCw, Loader2 } from 'lucide-react';
import { Formation, FieldPosition } from '../../utils/formations';
import PositionSelector from './PositionSelector';

interface Player {
  id: string;
  nome: string;
  posicao: string;
  nivel: number;
}

interface LineupPlayer extends Player {
  posicao_campo: string;
  x: number;
  y: number;
}

interface FieldVisualizerProps {
  formation: Formation | null;
  titularPlayers: LineupPlayer[];
  availablePlayers: Player[];
  onDropPlayer: (fieldPositionId: string, player: Player) => void;
  onRemovePlayer: (playerId: string) => void;
  onSave?: () => void;
  onClear?: () => void;
  saving?: boolean;
}

const FieldVisualizer: React.FC<FieldVisualizerProps> = ({
  formation,
  titularPlayers,
  availablePlayers,
  onDropPlayer,
  onSave,
  onClear,
  saving = false
}) => {
  const [showPositionSelector, setShowPositionSelector] = React.useState(false);
  const [selectedPosition, setSelectedPosition] = React.useState<{
    id: string;
    name: string;
    position: string;
  } | null>(null);

  const handleDrop = (e: React.DragEvent, fieldPositionId: string) => {
    e.preventDefault();
    try {
      const playerData = e.dataTransfer.getData('player');
      if (playerData) {
        const player = JSON.parse(playerData);
        onDropPlayer(fieldPositionId, player);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handlePositionClick = (fieldPos: FieldPosition) => {
    const existingPlayer = getPlayerInPosition(fieldPos.id);
    if (existingPlayer) {
      onRemovePlayer(existingPlayer.id);
      return;
    }

    setSelectedPosition({
      id: fieldPos.id,
      name: fieldPos.name,
      position: fieldPos.position
    });
    setShowPositionSelector(true);
  };

  const handleSelectPlayer = (player: Player) => {
    if (selectedPosition) {
      onDropPlayer(selectedPosition.id, player);
    }
  };

  const getPlayerInPosition = (fieldPositionId: string): LineupPlayer | null => {
    return titularPlayers.find(p => p.posicao_campo === fieldPositionId) || null;
  };

  const getPositionColor = (position: string) => {
    switch (position) {
      case 'goleiro':
        return 'bg-yellow-500 border-yellow-600';
      case 'zagueiro':
        return 'bg-blue-500 border-blue-600';
      case 'lateral':
        return 'bg-purple-500 border-purple-600';
      case 'meio-campo':
        return 'bg-green-500 border-green-600';
      case 'atacante':
        return 'bg-red-500 border-red-600';
      default:
        return 'bg-gray-500 border-gray-600';
    }
  };

  if (!formation) {
    return (
      <div className="w-full h-[500px] bg-gray-100 dark:bg-dark-light rounded-lg flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Selecione uma formação para visualizar o campo
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="relative w-full h-[500px] rounded-lg overflow-hidden border-4 border-white"
        style={{
          backgroundImage: `repeating-linear-gradient(
            to bottom,
            #2e7d32 0px,
            #2e7d32 60px,
            #388e3c 60px,
            #388e3c 120px
          )`,
          backgroundSize: "100% 120px",
        }}
      >
        {/* Campo de futebol - linhas do campo */}
        <div className="absolute inset-0">
          {/* Linha central */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-white transform -translate-y-1/2"></div>
          {/* Círculo central */}
          <div className="absolute top-1/2 left-1/2 w-20 h-20 border-2 border-white rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
          {/* Área do goleiro */}
          <div className="absolute bottom-0 left-1/2 w-32 h-16 border-2 border-white border-b-0 transform -translate-x-1/2"></div>
          <div className="absolute top-0 left-1/2 w-32 h-16 border-2 border-white border-t-0 transform -translate-x-1/2"></div>
          {/* Grande área */}
          <div className="absolute bottom-0 left-1/2 w-48 h-32 border-2 border-white border-b-0 transform -translate-x-1/2"></div>
          <div className="absolute top-0 left-1/2 w-48 h-32 border-2 border-white border-t-0 transform -translate-x-1/2"></div>
        </div>

        {/* Posições dos jogadores */}
        {formation.fieldPositions.map((fieldPos) => {
          const player = getPlayerInPosition(fieldPos.id);

          return (
            <div
              key={fieldPos.id}
              onDrop={(e) => handleDrop(e, fieldPos.id)}
              onDragOver={handleDragOver}
              onClick={() => handlePositionClick(fieldPos)}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              style={{
                left: `${fieldPos.x}%`,
                top: `${fieldPos.y}%`,
              }}
            >
              {player ? (
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 ${getPositionColor(player.posicao)} shadow-lg`}
                  title={`${player.nome} - Clique para remover`}
                >
                  <div className="text-center">
                    <div className="text-xs leading-tight">
                      {player.nome.split(' ')[0]}
                    </div>
                    <div className="text-xs opacity-75">
                      {fieldPos.name.split(' ')[0]}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="w-16 h-16 rounded-full border-2 border-dashed border-white bg-green-600/50 flex items-center justify-center text-white text-xs font-bold hover:bg-green-600/70 hover:scale-105 transition-all"
                  title={`Arraste um jogador para ${fieldPos.name}`}
                >
                  <div className="text-center">
                    <Users className="w-4 h-4 mx-auto mb-1" />
                    <div className="text-xs leading-tight">
                      {fieldPos.name.split(' ')[0]}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Informações da formação */}
        <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-2 rounded-lg">
          <p className="font-bold">{formation.name}</p>
          <p className="text-xs">{formation.description}</p>
        </div>

        {/* Contador de jogadores */}
        <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-2 rounded-lg">
          <p className="text-xs">
            {titularPlayers.length}/{formation.totalPlayers + 1} jogadores
          </p>
        </div>

        {/* Botões Flutuantes */}
        {titularPlayers.length > 0 && onSave && onClear && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3">
            <button
              onClick={onClear}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg transition-all duration-200 hover:scale-105 flex items-center gap-2"
              title="Limpar escalação"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="text-sm font-medium">Limpar</span>
            </button>

            <button
              onClick={onSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg shadow-lg transition-all duration-200 hover:scale-105 flex items-center gap-2"
              title="Salvar escalação"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-medium">Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span className="text-sm font-medium">Salvar</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Position Selector Modal */}
      <PositionSelector
        isOpen={showPositionSelector}
        onClose={() => setShowPositionSelector(false)}
        position={selectedPosition}
        availablePlayers={availablePlayers}
        onSelectPlayer={handleSelectPlayer}
      />
    </>
  );
};

export default FieldVisualizer;
